import React, { useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, MapPin, CalendarDays, Share2 } from 'lucide-react';
import PageHeader from '../components/PageHeader';
import SEO from '../components/SEO';
import { useSiteData } from '../lib/SiteDataContext';
import { useLang } from '../lib/LanguageContext';
import { resolveImage } from '../lib/api';

const EventDetailPage = () => {
  const { id } = useParams();
  const { events } = useSiteData();
  const { t } = useLang();
  useEffect(() => { window.scrollTo(0, 0); }, [id]);

  const item = (events || []).find((e) => e.id === id);

  if (!item) {
    return (
      <>
        <PageHeader title={t('Event not found')} crumbs={[{ label: 'Events', to: '/events' }, { label: '—' }]} />
        <section className="bg-[#FAF6EC] py-16">
          <div className="mx-auto max-w-[880px] px-6 text-center">
            <p className="text-[#0B2545]/70">{t('This event may have been removed or moved.')}</p>
            <Link
              to="/events"
              data-testid="event-back-to-list"
              className="mt-6 inline-flex items-center gap-2 rounded-full border border-[#C9A86A] bg-white px-5 py-2 text-sm font-semibold text-[#0B2545] hover:bg-[#F6F2EA]"
            >
              <ArrowLeft size={15} /> {t('Back to Events')}
            </Link>
          </div>
        </section>
      </>
    );
  }

  const startDate = `${item.year}-${item.month}-${item.day}`;

  return (
    <>
      <SEO
        title={item.title}
        description={(item.description || '').slice(0, 200)}
        image={item.image_url ? resolveImage(item.image_url) : undefined}
        type="article"
        jsonLd={{
          '@context': 'https://schema.org',
          '@type': 'Event',
          name: item.title,
          startDate,
          location: item.location ? { '@type': 'Place', name: item.location } : undefined,
          description: item.description,
          image: item.image_url ? resolveImage(item.image_url) : undefined,
        }}
      />
      <PageHeader
        title={item.title}
        crumbs={[{ label: 'Events', to: '/events' }, { label: item.title }]}
      />
      <article className="bg-[#FAF6EC] py-14" data-testid="event-detail">
        <div className="mx-auto max-w-[880px] px-6">
          <Link
            to="/events"
            data-testid="event-back-link"
            className="mb-6 inline-flex items-center gap-2 text-[13px] font-medium text-[#0B2545] hover:text-[#C9A86A]"
          >
            <ArrowLeft size={14} /> {t('Back to Events')}
          </Link>

          {item.image_url && (
            <div className="mb-8 overflow-hidden rounded-lg border border-[#E6DDC8] bg-white shadow-sm">
              <img
                src={resolveImage(item.image_url)}
                alt={item.title}
                className="h-[380px] w-full object-cover"
              />
            </div>
          )}

          <div className="rounded-lg border border-[#E6DDC8] bg-white p-8 shadow-sm">
            <div className="flex flex-wrap items-center gap-4 border-b border-[#E6DDC8] pb-6 text-[13px] text-[#0B2545]/70">
              <div className="inline-flex items-center gap-2">
                <CalendarDays size={15} className="text-[#C9A86A]" />
                <span className="font-medium text-[#0B2545]">
                  {item.day} {item.month} {item.year}
                </span>
              </div>
              {item.location && (
                <div className="inline-flex items-center gap-2">
                  <MapPin size={15} className="text-[#C9A86A]" />
                  {item.location}
                </div>
              )}
            </div>

            <p className="mt-6 whitespace-pre-line text-[15px] leading-relaxed text-[#0B2545]/85">
              {item.description}
            </p>

            <div className="mt-8 flex flex-wrap items-center justify-between gap-3 border-t border-[#E6DDC8] pt-6">
              <span className="text-[12px] uppercase tracking-widest text-[#0B2545]/50">
                {t('Official State House Event')}
              </span>
              <button
                type="button"
                data-testid="event-share-btn"
                onClick={() => {
                  const url = window.location.href;
                  if (navigator.share) {
                    navigator.share({ title: item.title, url });
                  } else if (navigator.clipboard) {
                    navigator.clipboard.writeText(url);
                  }
                }}
                className="inline-flex items-center gap-2 rounded-full border border-[#C9A86A] bg-white px-4 py-1.5 text-[12.5px] font-semibold text-[#0B2545] hover:bg-[#F6F2EA]"
              >
                <Share2 size={13} /> {t('Share')}
              </button>
            </div>
          </div>
        </div>
      </article>
    </>
  );
};

export default EventDetailPage;
